/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import MenuAim from "@monsma/MobiusOverrides/Menu/MenuAim";
import MenuController from "@monsma/MobiusOverrides/Menu/MenuController";
import FancyScroll from "@monsma/XDL/Components/FancyScroll";
import { hideScrollBar } from "@monsma/XDL/Utilities/sharedStyles";
import isMobile from "ismobilejs";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useRef } from "react";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
    iconProps?: IconPresentationProps;
}

export interface MenuPresentationProps {
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        // xng-change
        isPrimaryNavigation?: boolean;
        hasInternals?: boolean;
    }
>;

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps & MenuPresentationProps & ThemeProps<BaseTheme>;

type MenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
    };

// eslint-disable-next-line no-unexpected-multiline
const MenuStyle = styled.ul<MenuStyleProps>`
    background: ${getColor("common.background")};
    box-shadow: ${getProp("theme.shadows.5")};
    list-style: none;
    height: 470px;
    max-height: 60vh;
    padding: 25px 0 25px 0;
    border-radius: 0 0 6px 6px;
    margin-top: 6px;
    
    ${({ hasInternals }: any) => hasInternals && `height: auto;`}
    
    ${({ isPrimaryNavigation }: any) =>
        isPrimaryNavigation &&
        `
        overflow-x: hidden;
        overflow-y: auto;
        ${hideScrollBar}
        width: 300px;
        `}

    ${({ isPrimaryNavigation }: any) => !isPrimaryNavigation && `min-height: 200px;min-width: 200px;height: auto;`}

    ${({ currentDepth, isPrimaryNavigation }: any) =>
        currentDepth !== 1 &&
        isPrimaryNavigation &&
        css`
            background: ${getColor("common.border")};
            border-radius: 0 0 6px 0;
            &:after {
                content: "";
                width: 5px;
                height: 5px;
                position: absolute;
                bottom: 0;
                left: -5px;
                background: ${currentDepth > 2 ? getColor("common.border") : "white"};
            }
        `}

        .up {
            top: 6px;
            border-radius: 0;
        }

    ${injectCss}
`;

const MenuStyleWrapper = styled.div`
    position: absolute;
    z-index: ${getProp("theme.zIndex.menu")};
    margin: 0;
    padding: 0;
    min-width: ${getProp("width", 175)}px;
    transition: all 300ms ease;
    opacity: 0;
    pointer-events: none;
`;

type MenuWrapperProps = { canBeOpen: boolean; isOpen?: true } & InjectableCss;

const MenuWrapper = styled.nav<MenuWrapperProps>`
    position: relative; /* stylelint-disable */
    &.active > ${MenuStyleWrapper} {
        opacity: 1;
        pointer-events: all;
    }

    ${injectCss}
`;

// eslint-disable-next-line no-unexpected-multiline
type MenuItemStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        onClick: () => void;
    };

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean }>`
    padding: 7px 20px 7px 20px;
    color: ${getColor("text.main")};
    display: block;
    box-sizing: border-box;
    width: 100%;
    position: relative;
    font-size: 13px;
    font-weight: 600;
`;

const IconMemoStyled = styled(IconMemo)<IconPresentationProps>`
    top: 7px;
    height: 20px;
    width: 20px;
    color: ${getColor("common.ui9")};
`;

const MenuItemStyle = styled.li<MenuItemStyleProps>`

    ${({ isPrimaryNavigation }: any) => !isPrimaryNavigation && `position: relative;`}

    transition: all 200ms ease;
    ${MenuStyleWrapper} {
        left: 100%;
        top: 0;
    }
    &.active > ${MenuStyleWrapper}, 
    
    &.focus-within > ${MenuStyleWrapper} {
        display: block;
        opacity: 1;
        pointer-events: all;
    } 

    &:hover {
        background: ${getColor("common.ui8")};
    }
    &.active {
        background: ${getColor("common.ui3")};
        & > a > ${MenuItemText}, & > a > ${MenuItemText} > ${IconMemoStyled} {
            color: white;
        }
    }
    a {
        width: 100%;
        min-height: 34px;
        justify-content: space-between;
        transition: all 100ms ease;
    }
    ${injectCss}
`;

const trigger = React.createRef<HTMLElement>();

interface MenuState {
    canBeOpen: boolean;
    isMobile: boolean;
    shouldRender: boolean;
}

const RenderMenuItems = ({
    childMenuItems,
    currentDepth,
    cssOverrides,
    maxDepth = 3,
    applyProp,
    otherProps,
    shouldRender,
    displayChangeCustomerLink,
    isPrimaryNavigation,
    hasInternals,
}: {
    childMenuItems: MappedLink[];
    currentDepth: number;
    cssOverrides: any;
    maxDepth?: number;
    applyProp: any;
    otherProps: any;
    shouldRender: boolean;
    displayChangeCustomerLink?: boolean;
    isPrimaryNavigation?: boolean;
    hasInternals?: boolean;
}) => {
    const aimOptions = {
        tolerance: 180,
        activate: (row: HTMLElement) => {
            row.classList.add("active");
        },
        deactivate: (row: HTMLElement) => {
            row.classList.remove("active");
            row.querySelector("ul")?.dispatchEvent(new Event("reset"));
        },
        click: (row: HTMLElement) => {
            MenuController.closeAll();
            row.querySelector("ul")?.dispatchEvent(new Event("reset"));
        },
    };

    const ulRef = useRef<HTMLUListElement>(null);
    const aimRef = useRef<any>(null);

    const delayedAimInjection = useCallback(
        debounce(() => {
            if (ulRef && ulRef.current) {
                if (aimRef.current?.unHook) {aimRef.current.unHook();}
                aimRef.current = MenuAim(ulRef.current, aimOptions);
            }
        }, 1000),
        [],
    );

    useEffect(() => {
        delayedAimInjection();
    }, [ulRef, childMenuItems.length]);

    return (
        <MenuStyleWrapper>
            <MenuStyle
                width={otherProps.width}
                css={cssOverrides?.menu}
                ref={ulRef}
                {...({ isPrimaryNavigation, hasInternals, currentDepth } as any)}
            >
                {isPrimaryNavigation && <FancyScroll parentRef={ulRef} />}
                {childMenuItems
                    ?.filter(item => !item.excludeFromNavigation)
                    .map((item, index) => {
                        const hasChildren =
                            item.children?.some(o => !o.excludeFromNavigation) && currentDepth < maxDepth;
                        if ((item.type === "ChangeCustomerPage" && !displayChangeCustomerLink) || !shouldRender) {
                            return null;
                        }
                        if (shouldRender) {
                            return (
                                <MenuItemStyle
                                    width={otherProps.width}
                                    css={cssOverrides?.menuItem}
                                    key={`${item.title}_${index}`}
                                    data-test-selector="menuItem"
                                    {...({ isPrimaryNavigation } as any)}
                                >
                                    <Clickable 
                                        href={item.url}
                                        target={item.openInNewWindow ? '_blank' : ''}
                                        {...applyProp("menuItemClickableProps")}
                                        {...item.clickableProps}
                                    >
                                        {item.iconProps && <IconMemo {...item.iconProps} />}
                                        <MenuItemText
                                            hasChildren={hasChildren}
                                            {...applyProp("menuItemTypographyProps")}
                                            data-test-selector="menuItemText"
                                        >
                                            {item.title}
                                            {hasChildren && <IconMemoStyled {...applyProp("moreIconProps")} />}
                                        </MenuItemText>
                                    </Clickable>
                                    {hasChildren ? (
                                        <RenderMenuItems
                                            childMenuItems={item!.children!}
                                            currentDepth={currentDepth + 1}
                                            {...{
                                                cssOverrides,
                                                maxDepth,
                                                applyProp,
                                                otherProps,
                                                shouldRender,
                                                displayChangeCustomerLink,
                                                isPrimaryNavigation,
                                                hasInternals,
                                            }}
                                        />
                                    ) : null}
                                </MenuItemStyle>
                            );
                        }
                        return null;
                    })}
            </MenuStyle>
        </MenuStyleWrapper>
    );
};

/**
 * A cascading menu that renders a defined number of sub-menus.
 */
class Menu extends React.Component<MenuProps, MenuState> {
    static defaultProps: Partial<MenuProps> = {
        maxDepth: 3,
        width: 175,
    };

    state: MenuState = {
        canBeOpen: true,
        isMobile: false,
        shouldRender: true,
    };

    componentDidMount() {
        this.setState({
            isMobile: isMobile().any,
        });
    }

    componentWillUnmount() {}

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const {
                        menuItems,
                        menuTrigger,
                        isOpen,
                        isPrimaryNavigation,
                        hasInternals,
                        ...otherProps
                    } = this.props;
                    const { applyProp, spreadProps } = applyPropBuilder(
                        { ...otherProps, theme },
                        { component: "menu" },
                    );

                    const cssOverrides = spreadProps("cssOverrides");
                    const descriptionId = otherProps.descriptionId || uniqueId();

                    const openEvent = (e: any) => {
                        // console.log("mouseOverEvent", e);
                        const elm = e.currentTarget;
                        MenuController.registerOpen(elm, (current: HTMLElement) => {
                            if (isPrimaryNavigation) {
                                document.querySelector("body")?.classList?.add("shade");
                            } else {
                                document.querySelector("body")?.classList?.remove("shade");
                                current.querySelector("ul")?.dispatchEvent(new Event("reset"));
                            }
                        });
                    };
                    const closeEvent = (e: any, timeout: number = MenuController.closeTime) => {
                        const elm = e.currentTarget;
                        MenuController.registerClose(
                            elm,
                            (current: HTMLElement) => {
                                elm.querySelector("ul")?.dispatchEvent(new Event("reset"));
                                document.querySelector("body")?.classList?.remove("shade");
                            },
                            timeout,
                        );
                    };

                    return (
                        <>
                            <MenuWrapper
                                canBeOpen={this.state.canBeOpen}
                                isOpen={isOpen}
                                css={cssOverrides?.wrapper}
                                {...{ "aria-labelledby": descriptionId }}
                                {...otherProps}
                                onMouseOverCapture={openEvent}
                                onMouseOut={closeEvent}
                                onFocus={(e: any) => {
                                    if (!e.currentTarget.contains(e.relatedTarget)) {
                                        openEvent(e);
                                    }
                                }}
                                onBlur={(e: any) => {
                                    if (!e.currentTarget.contains(e.relatedTarget)) {
                                        closeEvent(e, 0);
                                    }
                                }}
                                onClick={(e: any) => {
                                    // check if user clicked on the nav trigger
                                    if (e.currentTarget?.querySelector(".trigger")?.contains(e.target)) {
                                        closeEvent(e, 20);
                                    }
                                }}
                            >
                                {React.cloneElement(menuTrigger, { id: descriptionId, className: "trigger" })}
                                <RenderMenuItems
                                    childMenuItems={menuItems}
                                    currentDepth={1}
                                    maxDepth={otherProps.maxDepth}
                                    shouldRender={this.state.shouldRender}
                                    displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                                    {...{ cssOverrides, applyProp, otherProps, isPrimaryNavigation, hasInternals }}
                                />
                            </MenuWrapper>
                            <span ref={trigger} tabIndex={-1} />
                        </>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/** @component */
export default withTheme(Menu);

export { MenuItemText };
